"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail, Clock, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function BookingFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const ghostInputClass = "w-full bg-transparent border-0 border-b-2 border-border/50 focus:border-primary focus:ring-0 px-0 py-2 font-sans text-base text-foreground outline-none transition-all placeholder:text-muted-foreground/50 disabled:opacity-50";
  const labelClass = "font-sans text-[12px] uppercase tracking-widest text-primary font-bold mb-1 block";
  if (isSubmitted && !submitError) {
    return <div data-section-id="101673" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
                <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(34,197,94,0.3)]">
                    <CircleCheck className="size-8 text-primary" />
                </div>
                <h3 className="font-sans text-2xl font-extrabold text-foreground uppercase tracking-tight">Request Received!</h3>
                <p className="font-sans text-muted-foreground max-w-md">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 font-sans uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    Send Another Message
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3">
                    <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive font-sans">{submitError}</p>
                </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="full_name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className={labelClass}>Full Name *</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="Alex Johnson" className={ghostInputClass} disabled={isSubmitting} {...field} />
                            </FormControl>
                            <FormDescription className="font-sans text-[10px] text-muted-foreground mt-1 uppercase tracking-tighter">
                                Enter your full name or business representative.
                            </FormDescription>
                            <FormMessage className="font-sans text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className={labelClass}>Business Email *</FormLabel>
                            <FormControl>
                                <input type="email" placeholder="alex@company.com" className={ghostInputClass} disabled={isSubmitting} {...field} />
                            </FormControl>
                            <FormMessage className="font-sans text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className={labelClass}>Phone Number</FormLabel>
                            <FormControl>
                                <input type="tel" placeholder="+31 6 1234 5678" className={ghostInputClass} disabled={isSubmitting} {...field} />
                            </FormControl>
                            <FormDescription className="font-sans text-[10px] text-muted-foreground mt-1 uppercase tracking-tighter">
                                Optional – for urgent partnership inquiries.
                            </FormDescription>
                            <FormMessage className="font-sans text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="activity" rules={{
        required: "Select an inquiry type"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className={labelClass}>Inquiry Type *</FormLabel>
                            <FormControl>
                                <select className={cn(ghostInputClass, "appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2322c55e%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[length:1.2em_1.2em] pr-8")} disabled={isSubmitting} {...field}>
                                    <option value="" disabled>Select a reason</option>
                                    <option value="Venue Partnership">Venue Partnership</option>
                                    <option value="Event Rental">Event Rental</option>
                                    <option value="Referral Program">Referral Program</option>
                                    <option value="Support">Technical Support</option>
                                </select>
                            </FormControl>
                            <FormMessage className="font-sans text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="adventure_date" rules={{
        required: "Date is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className={labelClass}>Target Launch Date *</FormLabel>
                            <FormControl>
                                <input type="date" className={ghostInputClass} disabled={isSubmitting} {...field} />
                            </FormControl>
                            <FormDescription className="font-sans text-[10px] text-muted-foreground mt-1 uppercase tracking-tighter">
                                When would you like to start renting?
                            </FormDescription>
                            <FormMessage className="font-sans text-xs text-destructive" />
                        </FormItem>} />

                <FormField control={control} name="participants" rules={{
        required: "Station count is required",
        min: {
          value: 1,
          message: "Minimum 1 station"
        },
        max: {
          value: 10,
          message: "Maximum 10 stations per venue"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
                            <FormLabel className={labelClass}>Stations Needed *</FormLabel>
                            <FormControl>
                                <input type="number" min="1" max="10" placeholder="1" className={ghostInputClass} disabled={isSubmitting} {...field} />
                            </FormControl>
                            <FormDescription className="font-sans text-[10px] text-muted-foreground mt-1 uppercase tracking-tighter">
                                Number of charging units requested.
                            </FormDescription>
                            <FormMessage className="font-sans text-xs text-destructive" />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="space-y-1">
                        <FormLabel className={labelClass}>Message or Specific Requirements</FormLabel>
                        <FormControl>
                            <textarea placeholder="Tell us about your venue or specific event needs..." rows={3} className={cn(ghostInputClass, "resize-none")} disabled={isSubmitting} {...field} />
                        </FormControl>
                        <FormMessage className="font-sans text-xs text-destructive" />
                    </FormItem>} />

            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[220px] h-14 bg-primary text-primary-foreground font-sans uppercase tracking-widest font-extrabold hover:translate-y-[-4px] transition-all duration-300 shadow-[0_10px_20px_rgba(34,197,94,0.3)] hover:shadow-[0_15px_30px_rgba(34,197,94,0.4)] rounded-full">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Transmitting...
                    </> : 'Power Up Now'}
            </Button>
        </Form>;
}
export default function StartJeAvontuur() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: leftColRef,
    isVisible: isLeftColVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: rightColRef,
    isVisible: isRightColVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const contactDetails = [{
    icon: MapPin,
    title: "Headquarters",
    content: "Stadhouderskade 1, 1054 ES Amsterdam",
    isLink: false
  }, {
    icon: Phone,
    title: "Support Line",
    content: "+31 (0) 20 123 4567",
    isLink: true,
    href: "tel:31201234567"
  }, {
    icon: Mail,
    title: "General Inquiry",
    content: "hello@zapgo!.nl",
    isLink: true,
    href: "mailto:hello@zapgo!.nl"
  }, {
    icon: Clock,
    title: "Business Hours",
    content: "Mon - Sun: 08:00 - 22:00",
    isLink: false
  }];
  return <section id="contact" className="bg-background py-20 md:py-32 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {}
                <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-16 md:mb-24 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
                    <h2 className="font-sans text-5xl md:text-7xl font-extrabold text-foreground mb-6 uppercase tracking-tighter">
                        Stay <span className="text-primary">Connected</span>
                    </h2>
                    <p className="font-sans text-xl text-muted-foreground leading-relaxed">
                        Whether you're a venue owner looking to boost revenue or a user in need of a charge, our team is ready to power your day. Let's talk energy.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    
                    {}
                    <div ref={leftColRef} className={cn("lg:col-span-7 transition-all duration-1000 delay-200 ease-out", isLeftColVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12")}>
                        <div className="bg-card p-8 md:p-12 rounded-[2.5rem] shadow-2xl shadow-primary/5 border border-border/40 backdrop-blur-sm">
                            <h3 className="font-sans text-3xl font-bold text-foreground mb-8 uppercase tracking-tight">
                                Partnership <span className="text-primary">Inquiry</span>
                            </h3>
                            
                            <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="contact" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              activity: "",
              adventure_date: "",
              participants: 1,
              message: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              activity: {
                required: true
              },
              adventure_date: {
                required: true
              },
              participants: {
                required: true,
                min: 1,
                max: 10
              }
            }} submitText="Power Up Now" successMessage={<>Success! Your energy request has been logged. A <span style={{
                color: '#ffe838'
              }}>Zap</span><span style={{
                color: '#70cf70'
              }}>GO!</span> specialist will reach out within 24 hours to finalize your setup.</>} errorMessage="Connection failed. Please verify your details and try transmitting your request again.">
                                <BookingFormInner />
                            </FormProvider>
                        </div>
                    </div>

                    {}
                    <div ref={rightColRef} className={cn("lg:col-span-5 flex flex-col gap-10 transition-all duration-1000 delay-400 ease-out", isRightColVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12")}>
                        {}
                        <div className="bg-card/30 p-8 rounded-[2rem] border border-border/20">
                            <h3 className="font-sans text-2xl font-bold text-foreground mb-8 uppercase tracking-tight">
                                Reach <span className="text-primary"><span style={{
                  color: '#ffe838'
                }}>Zap</span><span style={{
                  color: '#70cf70'
                }}>GO!</span></span>
                            </h3>
                            <div className="space-y-8">
                                {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-5 group">
                                        <div className="size-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 group-hover:shadow-[0_0_15px_rgba(34,197,94,0.2)] transition-all duration-300">
                                            <item.icon className="size-6 text-primary" />
                                        </div>
                                        <div className="pt-1">
                                            <h4 className="font-sans text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-1">
                                                {item.title}
                                            </h4>
                                            {item.isLink ? <Link to={item.href || "#"} className="font-sans text-lg font-medium text-foreground hover:text-primary transition-colors duration-200">
                                                    {item.content}
                                                </Link> : <p className="font-sans text-lg font-medium text-foreground">
                                                    {item.content}
                                                </p>}
                                        </div>
                                    </div>)}
                            </div>
                        </div>

                        {}
                        <div className="flex-grow min-h-[300px] w-full rounded-[2rem] overflow-hidden shadow-2xl shadow-primary/10 border border-border/40 relative group">
                            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2436.1234!2d4.895!3d52.370!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTLCsDIyJzEyLjAiTiA0wrA1MydNDIuMCJF!5e0!3m2!1sen!2snl!4v1620000000000!5m2!1sen!2snl" width="100%" height="100%" style={{
              border: 0,
              position: 'absolute',
              top: 0,
              left: 0,
              filter: 'grayscale(0.5) contrast(1.2)'
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="ZapGO! HQ Location" className="absolute inset-0 w-full h-full group-hover:grayscale-0 transition-all duration-700"></iframe>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}